import {
    HorizAlignment,
    IDisplayJoinField,
    ILockField,
    IProfileFilter,
    ISearch,
    ISearchProfile,
    ISearchProfileRenderOptions,
    ISearchProvider,
    ISearchProviderField,
    ISearchRenderOptions,
    ISearchRequestSubset,
    ISearchSort,
    SearchProviderFieldType,
} from "../PA.UIM.Core/PA.UIM.Core.IF.Search";
import { WB_SearchProfileCRRow, WB_SearchProfileRow, WB_SearchRow } from "../PA.Core/PAdbSchemaRows";
import * as _ from "lodash";
import { PAdbContext } from "../PA.Core/PAdbContext";
import { ESRecordResolver } from "../PA.Core/lib/ESRecordResolver";
import {
    esWB_Search,
    esWB_Search_Profile,
    esWB_Search_ProfileCR,
    esWB_Search_ProfileFCR,
    esWB_Search_ProfileGCR,
} from "../PA.Core/PAdbSchema";
import { SearchUtils } from "../PA.UIM.Core/SearchUtils";
import { ASync } from "../PA.Core/Basic/ASync";
import { PASearch } from "../PA.Core/PASearch";

class SearchProvider implements ISearchProvider {
    searchProfileRow: WB_SearchProfileRow;
    vUI_JSON: object;
    vUI_SQL: string;

    constructor(searchProfileRow: WB_SearchProfileRow) {
        this.searchProfileRow = searchProfileRow;
        this.vUI_SQL = searchProfileRow.cif.SQL_QUERY;
    }

    get keyFields() {
        let id: string, id_ui: string, name: string;
        this.searchProfileRow.WB_SearchProfileCRRows.forEach((column) => {
            if (column.cif.COLUMN_METATYPE == "id") id = column.cif.COLUMN_ID;
            else if (column.cif.COLUMN_METATYPE == "id_ui") id_ui = column.cif.COLUMN_ID;
            else if (column.cif.COLUMN_METATYPE == "name") name = column.cif.COLUMN_ID;
        });
        let fields = {
            id: id,
            id_ui: id_ui,
            name: name,
        };
        return fields;
    }

    get searchProviderFields(): {
        [field_id: string]: ISearchProviderField;
    } {
        let fields: { [field_id: string]: ISearchProviderField } = {};
        let gcrRowsKeyById = _.keyBy(this.searchProfileRow.WB_SearchProfileGCRRows, (aRow) => aRow.cif.COLUMN_ID);
        this.searchProfileRow.WB_SearchProfileCRRows.forEach((column) => {
            let columnDataType = column.cif.DISPLAYTYPE;
            let type: SearchProviderFieldType = undefined;

            switch (columnDataType) {
                case "number":
                    type = SearchProviderFieldType.number;
                    break;
                case "text":
                    type = SearchProviderFieldType.string;
                    break;
                case "boolean":
                    type = SearchProviderFieldType.boolean;
                    break;
                case "date":
                    type = SearchProviderFieldType.date;
                    break;
                case "sapdate":
                    type = SearchProviderFieldType.sapdate;
                    break;
                case "sapdatetime":
                    type = SearchProviderFieldType.saptimestamp;
                    break;
                default:
                    type = SearchProviderFieldType.string;
                    break;
            }

            let align: HorizAlignment = undefined;
            switch (column.cif.ALIGNMENT) {
                case "Left":
                    align = HorizAlignment.Left;
                    break;
                case "Right":
                    align = HorizAlignment.Right;
                    break;
                case "Center":
                    align = HorizAlignment.Center;
                    break;
                default:
                    align = HorizAlignment.Left;
                    break;
            }

            let sColName = "";
            if (column.cif.COLUMN_ID in gcrRowsKeyById) sColName = gcrRowsKeyById[column.cif.COLUMN_ID].cif.COLUMN_NAME;
            else sColName = column.cif.COLUMN_NAME;

            let item: ISearchProviderField = {
                type: type,
                renderOptions: {
                    columnName: sColName,
                    alignment: align,
                    columnWidthCharacters: column.cif.COLUMNWIDTHCHARS,
                    dateFormat: column.cif.DISPLAYFORMAT,
                    numberFormat: column.cif.DISPLAYFORMAT,
                },
                domainOptions: column.cif.DISPLAYDOMAIN != "" ? JSON.parse(column.cif.DISPLAYDOMAIN) : {},
            };
            fields[column.cif.COLUMN_ID] = item;
        });
        return fields;
    }

    search(requestSpec: ISearchRequestSubset): Promise<any[]> {
        let self = this;
        let opts = requestSpec;
        let whereClause = "";

        if ("where" in opts) {
            var fields: string[] = [];

            let arrFilters = [];

            //check if multiple filters
            let rootCond = Object.keys(opts.where)[0];
            if (rootCond == "and") arrFilters = opts.where["and"];
            else arrFilters = [opts.where];

            _.forEach(arrFilters, (aCond) => {
                var sqlCond = "";
                let condColumn = Object.keys(aCond)[0];
                if (_.isUndefined(condColumn)) return;

                let condObj = aCond[condColumn];

                if (condObj.constructor.name === "Object") {
                    let condType = Object.keys(condObj)[0];
                    if (_.isUndefined(condType)) return;

                    let field = self.searchProviderFields[condColumn];
                    let filterValue = condObj[condType];

                    if (field.type == SearchProviderFieldType.saptimestamp) {
                        filterValue += "000000";
                    }

                    switch (condType) {
                        case "inq":
                            sqlCond += condColumn + " IN ";
                            break;
                        case "nin":
                            sqlCond += condColumn + " NOT IN ";
                            break;
                        case "like":
                            sqlCond += condColumn + " LIKE '";
                            filterValue += "'";
                            break;
                        case "ilike":
                            sqlCond = "UPPER(" + condColumn + ") ";
                            sqlCond += " LIKE '";
                            filterValue = filterValue.toUpperCase();
                            filterValue += "'";
                            break;
                        case "nlike":
                            sqlCond += condColumn + "  NOT LIKE '";
                            filterValue += "'";
                            break;
                        case "nilike":
                            sqlCond = "UPPER(" + condColumn + ") ";
                            sqlCond += " NOT LIKE '";
                            filterValue = filterValue.toUpperCase();
                            filterValue += "'";
                            break;
                        case "qt":
                            sqlCond += condColumn + " > ";
                            break;
                        case "qte":
                            sqlCond += condColumn + " >= ";
                            break;
                        case "lt":
                            sqlCond += condColumn + " < ";
                            break;
                        case "lte":
                            sqlCond += condColumn + " <= ";
                            break;
                        case "neq":
                            sqlCond += condColumn + " != ";
                            break;
                        default:
                            sqlCond += " " + condType + " ";
                            break;
                    }

                    sqlCond +=
                        condType === "inq" || condType === "nin"
                            ? "(" + _.map(filterValue, (aVal) => "'" + aVal + "'").join() + ")"
                            : filterValue;
                    fields.push(sqlCond);
                } else {
                    fields.push(_.escape(condColumn) + " = " + condObj);
                }
            });
            whereClause = fields.join(" AND ");
        }

        let sql = this.vUI_SQL;
        /*if(whereClause != '')
            sql = sql + " where " + whereClause*/

        let columns = this.searchProfileRow.WB_SearchProfileCRRows;
        let colTypeByName = _.keyBy(columns, (aRow: WB_SearchProfileCRRow) => aRow.cif.COLUMN_ID);

        let stringDataTypes = ["VARCHAR", "NVARCHAR", "ALPHANUM", "SHORTTEXT"];

        return new Promise<any[]>((resolve, error) => {
            let findRequest = SearchUtils.makeSQLSearchRequest(sql, whereClause, this.searchProfileRow.cif.ROW_LIMIT);

            PASearch.findByRequest(findRequest)
                .then((res: any[]) => {
                    _.forEach(res, (aRes) => {
                        for (let key in aRes) {
                            let col = colTypeByName[key];
                            if (!_.isUndefined(col)) {
                                let colType = col.cif.COLUMN_DATATYPE;
                                if (stringDataTypes.indexOf(colType) == -1) {
                                    let parsedValue = parseFloat(aRes[key]);
                                    if (!_.isNaN(parsedValue)) aRes[key] = parsedValue;
                                }
                            }
                        }
                    });
                    resolve(res);
                })
                .catch((err) => {
                    error(err);
                });
        });
    }

    distinctValues(fieldName: string): Promise<string[] | number[]> {
        let sql = this.vUI_SQL;

        return new Promise<any[]>((resolve, error) => {
            let findRequest = SearchUtils.makeSQLSearchRequest(sql);

            PASearch.findByRequest(findRequest)
                .then((res: any[]) => {
                    let values = _.map(res, (aRow) => {
                        return aRow[fieldName];
                    });
                    resolve(_.uniq(values));
                })
                .catch((err) => {
                    error(err);
                });
        });
    }
}

class SearchProfile implements ISearchProfile {
    searchProfileRow: WB_SearchProfileRow;
    profileId: string;
    profileName: string;
    renderOptions: ISearchProfileRenderOptions;
    profileSearchProvider: ISearchProvider;
    profileSortIdDefault: string;
    profileSorts: ISearchSort[];

    constructor(searchProfileRow: WB_SearchProfileRow, SearchProvider: ISearchProvider) {
        this.searchProfileRow = searchProfileRow;
        this.profileSearchProvider = SearchProvider;
        this.profileId = searchProfileRow.cif.PROFILE_ID;
        this.profileName = searchProfileRow.cif.PROFILE_NAME;
    }

    get searchProvider() {
        return this.profileSearchProvider;
    }

    get profileFilter(): IProfileFilter[] {
        let filters: IProfileFilter[] = [];
        let sortedFilterFields = _.sortBy(
            this.searchProfileRow.WB_SearchProfileFCRRows,
            (aFCRRow) => aFCRRow.cif.SORT_ORDER,
        );
        _.forEach(sortedFilterFields, (aField) =>
            filters.push({
                profileFilterName: aField.cif.FILTER_NAME,
                searchProviderFieldId: aField.cif.COLUMN_ID,
                renderOptions: {},
                bMultiSelect: aField.cif.MULTISELECT_IND == "1" ? true : false,
                bHidden: aField.cif.HIDDEN_IND == "1" ? true : false,
                bRequired: aField.cif.REQUIRED_IND == "1" ? true : false,
            }),
        );

        return filters;
    }

    get profileDisplayFields(): (string | IDisplayJoinField | ILockField)[] {
        let displayFields: string[] = [];

        let sortedDisplayFields = _.sortBy(
            this.searchProfileRow.WB_SearchProfileGCRRows,
            (aCRRow) => aCRRow.cif.SORT_ORDER,
        );
        _.forEach(sortedDisplayFields, (aField) => displayFields.push(aField.cif.COLUMN_ID));

        return displayFields;
    }

    get profileRequiredFields(): (string | IDisplayJoinField | ILockField)[] {
        let requiredFields: string[] = [];

        let sortedRequiredFields = _.sortBy(
            this.searchProfileRow.WB_SearchProfileFCRRows,
            (aCRRow) => aCRRow.cif.SORT_ORDER,
        );
        _.forEach(sortedRequiredFields, (aField) => {
            if (aField.isActive && aField.cif.REQUIRED_IND == "1") requiredFields.push(aField.cif.COLUMN_ID);
        });

        return requiredFields;
    }
}

export class Search implements ISearch {
    _searchRow: WB_SearchRow;
    searchProfiles: ISearchProfile[];
    wbSearchId: number;
    wbProfiledId: number;
    searchName: string;
    renderOptions: ISearchRenderOptions;
    searchProfileIdDefault: string;
    _bIsLoaded: boolean;
    _OnLoadComplete: Promise<ISearch>;

    constructor(wbSearchId: number) {
        this.wbSearchId = wbSearchId;
        this.searchProfiles = [];
        this._bIsLoaded = false;
        this._OnLoadComplete = null;
    }

    get OnLoadComplete(): Promise<ISearch> {
        if (this._bIsLoaded) {
            return ASync.resolve(this);
        }
        return this._OnLoadComplete;
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded;
    }

    get WB_SearchRow(): WB_SearchRow {
        return this._searchRow;
    }

    async LoadSearch(wbSearchId: number, options?: { dbContext?: PAdbContext }): Promise<void> {
        let opts = options || {};
        let dbContext = opts.dbContext;
        if (_.isEmpty(dbContext)) dbContext = new PAdbContext();

        let rr = new ESRecordResolver(dbContext, {
            standardConditions: {
                [esWB_Search]: { DELETED_IND: { notIn: ["1"] } },
                [esWB_Search_Profile]: { DELETED_IND: { notIn: ["1"] } },
                [esWB_Search_ProfileCR]: { DELETED_IND: { notIn: ["1"] } },
                [esWB_Search_ProfileFCR]: { DELETED_IND: { notIn: ["1"] } },
                [esWB_Search_ProfileGCR]: { DELETED_IND: { notIn: ["1"] } },
            },
            dctExcludeHasManyNamedRelations: {},
            dctIncludeBelongsToNamedRelations: {},
        });

        try {
            await rr.findAllWithRelationships(esWB_Search, [wbSearchId]);
            let aRow: WB_SearchRow = <WB_SearchRow>rr.get(esWB_Search, wbSearchId);
            this._searchRow = aRow;

            this.searchName = aRow.cif.SEARCH_NAME;
            let defaultProfile = _.first(
                _.filter(
                    this.WB_SearchRow.WB_SearchProfileRows,
                    (profileRow) => profileRow.isActive && profileRow.cif.DEFAULT_IND == "1",
                ),
            );
            this.searchProfileIdDefault = defaultProfile.cif.PROFILE_ID;
            this.searchProfiles.push(new SearchProfile(defaultProfile, new SearchProvider(defaultProfile)));

            _.orderBy(
                _.filter(this.WB_SearchRow.WB_SearchProfileRows, (row) => row.isActive && row.cif.DEFAULT_IND != "1"),
                "SORT_ORDER",
            ).forEach((profileRow) => {
                this.searchProfiles.push(new SearchProfile(profileRow, new SearchProvider(profileRow)));
            });

            this._OnLoadComplete = new Promise<ISearch>((resolve, error) => {
                this._bIsLoaded = true;
                this._OnLoadComplete = null;
                resolve(this);
            });
        } catch (e) {
            console.error(e);
        }
    }
}
